import { Box } from "@baseline/ui/common";
import { useService } from "@baseline/ui/react";
import { View, Viewer } from "@baseline/ui/views";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ConfigurationService } from "../../config";
import { ViewManager } from "../../ui";
import { postMessageBetweenFrames } from "../../utils/post-message-between-frames";
import { InternalTokenApiClient } from "../internal-api";
import { CardService } from "../services";
import { SessionType } from "../adyen/session-types";
import { resolveSessionTypeFromPath } from "../utils/session-type-detector";

import { styles } from "./app-screen.scss";

const useUrlParams = () => {
  return useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const transactionId = searchParams.get("transactionId");
    const encodedSessionData = searchParams.get("sessionData");
    const sessionType = resolveSessionTypeFromPath();

    return {
      transactionId,
      encodedSessionData,
      sessionType,
      isValid: Boolean(encodedSessionData), // Only require sessionData
    };
  }, []);
};

export const CardDropIn: FC = () => {
  const [disabled, setDisabled] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const viewManager = useService(ViewManager);
  const { views } = viewManager;

  const { transactionId, encodedSessionData, sessionType, isValid } =
    useUrlParams();

  if (!isValid) {
    console.warn("Invalid URL parameters.");
  }

  const [token, setToken] = useState<string | null>(null);
  const configurationService = useService(ConfigurationService);
  const internalTokenApiClient = useService(InternalTokenApiClient);
  const cardService = useService(CardService);
  const componentRef = useRef<HTMLDivElement>(null);

  const initCardPayment = useCallback(async () => {
    cardService.initializeApiClients(
      configurationService.get("internalApiOrigin"),
      token,
    );

    if (sessionType === SessionType.TOKENIZATION) {
      await cardService.tokenizationSetup(encodedSessionData, componentRef);
    } else {
      if (!transactionId) {
        console.error("Transaction ID is required for payment sessions");
        return;
      }
      await cardService.paymentSetup(
        transactionId,
        encodedSessionData,
        componentRef,
      );
    }

    postMessageBetweenFrames({ type: "card-payment-setup" });
  }, [
    cardService,
    configurationService,
    token,
    transactionId,
    encodedSessionData,
    sessionType,
    componentRef,
  ]);

  useEffect(() => {
    if (!token) {
      console.warn("Token is not set yet.");
      return;
    }

    initCardPayment();
  }, [token, initCardPayment]);

  useEffect(() => {
    const getToken = async () => {
      const response =
        await internalTokenApiClient.createAuthToken(encodedSessionData);
      setToken(response.token);
    };

    getToken();
  }, [encodedSessionData, internalTokenApiClient]);

  const handleSubmit = useCallback(async () => {
    setDisabled(true);

    try {
      const { refusal } = await cardService.submitSession();
      if (refusal) {
        postMessageBetweenFrames({ type: "card-payment-failed" });
      } else {
        postMessageBetweenFrames({ type: "card-payment-completed" });
        setShowButton(false);
      }
    } catch (error) {
      if (error?.state?.isValid) {
        console.error({error});
        postMessageBetweenFrames({ type: "card-payment-error" });
      } else {
        console.warn("Component state is not valid.", { state: error.state });
        postMessageBetweenFrames({ type: "card-payment-form-invalid" });
      }
    } finally {
      setDisabled(false);
    }
  }, [cardService, disabled, setDisabled]);

  return (
    <Box className={styles.appScreen}>
      <Viewer>
        <View view={views.landingPage}>
          {isValid ? (
            <>
              <div ref={componentRef}></div>
              {showButton && (
                <button
                  type="button"
                  className="adyen-checkout__button adyen-checkout__button--pay"
                  onClick={handleSubmit}
                  disabled={disabled}
                >
                  {sessionType === SessionType.TOKENIZATION
                    ? "Add Card"
                    : "Submit Payment"}
                </button>
              )}
            </>
          ) : (
            <div>Invalid URL parameters.</div>
          )}
        </View>
      </Viewer>
    </Box>
  );
};
